import { Box, Stack, Typography } from '@mui/material';
import { BANDS } from './measurePaint';

/**
 * What the colours on the photo mean.
 *
 * Colour alone is never allowed to carry the finding. Someone who cannot tell
 * the bands apart — and that is a lot of people — has to get the same reading
 * from this block as everybody else gets from the picture, so every colour
 * drawn on the face is named here, and a graded area shows its own figure in
 * the reader's own numerals.
 *
 * For a graded measurement the scale itself is shown too, in the exact colours
 * the written report prints, because this is that report's colour map put back
 * on the face it was taken from. The thresholds are the report's: 90 and above,
 * 75 and above, below that.
 *
 * The wording describes a difference, never a person. "Closely matched" and
 * "noticeable difference" are observations about two sides of a face; "good"
 * and "poor" would be a verdict on someone's appearance, which is not this
 * page's business and not any bot's either.
 */
export default function MeasureLegend({ paint, t, lang = 'fa' }) {
  const rows = paint?.legend || [];
  if (!rows.length) return null;

  const fmt = (n) => new Intl.NumberFormat(lang === 'fa' ? 'fa-IR' : 'en-GB')
    .format(Math.round(n));

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="caption"
        sx={{ color: 'text.secondary', display: 'block', mb: .75 }}>
        {t(`candleCard.paint.caption.${paint.mode}`, {
          defaultValue: t('candleCard.paint.caption.plain'),
        })}
      </Typography>

      <Stack direction="row" flexWrap="wrap" sx={{ gap: .75 }}>
        {rows.map((row) => (
          <Stack
            key={`${row.kind}-${row.group}`}
            direction="row" alignItems="center" spacing={.75}
            sx={{
              px: 1, py: .5, borderRadius: 2,
              border: '1px solid', borderColor: 'divider',
              bgcolor: '#FFFFFF',
              // A stepped-back area is listed, but visibly secondary — the same
              // relationship it has to the drawing.
              opacity: row.muted ? 0.6 : 1,
            }}
          >
            <Box sx={{
              width: 11, height: 11, borderRadius: '50%', flexShrink: 0,
              bgcolor: row.color,
              boxShadow: '0 0 0 1.5px rgba(17,34,62,.16)',
            }} />
            <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
              {t(row.labelKey, { defaultValue: row.label })}
            </Typography>
            {row.score != null && (
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                {t('candleCard.paint.percent', { n: fmt(row.score) })}
              </Typography>
            )}
          </Stack>
        ))}
      </Stack>

      {paint.mode === 'graded' && (
        <Stack direction="row" flexWrap="wrap" alignItems="center"
          sx={{ gap: 1.25, mt: 1.25 }}>
          {BANDS.map((b) => (
            <Stack key={b.key} direction="row" alignItems="center" spacing={.5}>
              <Box sx={{
                width: 16, height: 6, borderRadius: 3,
                // The colour as it appears ON THE PHOTO, not the report's
                // printed shade of it. The scale's job here is to let someone
                // match a band to a dot a few centimetres above it, and two
                // tones of the same hue on one screen only muddies that.
                bgcolor: b.draw,
              }} />
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                {t(`candleCard.paint.band.${b.key}`)}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
}
